import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight, FiCamera, FiClock, FiUsers, FiFileText, FiZap } from 'react-icons/fi';
import { getDailySummary } from '../services/api';
import { API_BASE_URL } from '../config';

const fmtDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
const shiftDate = (d, days) => { const x = new Date(d + 'T00:00:00'); x.setDate(x.getDate() + days); return x.toISOString().slice(0, 10); };
const todayStr = () => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`; };
const fmtTime = (iso) => iso ? new Date(iso.endsWith('Z') ? iso : iso + 'Z').toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';

function Stat({ icon, value, label }) {
  return (
    <div style={{ flex: 1, minWidth: 90, textAlign: 'center', padding: '10px 6px' }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: '#111827' }}>{value}</div>
      <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>{icon}{label}</div>
    </div>
  );
}

export default function DailySummary() {
  const [dateStr, setDateStr] = useState(todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async (d) => {
    setLoading(true); setError('');
    try {
      const res = await getDailySummary(d);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load the summary.');
      setData(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(dateStr); }, [dateStr, load]);

  const isToday = dateStr === todayStr();

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 4px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', margin: '4px 0 16px' }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>Daily Summary</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button onClick={() => setDateStr(shiftDate(dateStr, -1))} className="btn btn-sm" title="Previous day"><FiChevronLeft /></button>
          <input type="date" value={dateStr} max={todayStr()} onChange={(e) => setDateStr(e.target.value)}
            style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }} />
          <button onClick={() => setDateStr(shiftDate(dateStr, 1))} className="btn btn-sm" disabled={isToday} title="Next day"><FiChevronRight /></button>
          {!isToday && <button onClick={() => setDateStr(todayStr())} className="btn btn-sm">Today</button>}
        </div>
      </div>

      <div style={{ color: '#6b7280', fontSize: 14, marginBottom: 16 }}>{fmtDate(dateStr)}</div>

      {loading ? (
        <div style={{ color: '#9ca3af', textAlign: 'center', padding: 40 }}>Loading…</div>
      ) : error ? (
        <div style={{ color: '#dc2626', textAlign: 'center', padding: 40 }}>{error}</div>
      ) : !data || data.totals.jobs === 0 ? (
        <div style={{ background: '#fff', borderRadius: 12, padding: 40, textAlign: 'center', color: '#6b7280', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          No job activity recorded for this day.
        </div>
      ) : (
        <>
          {/* AI recap */}
          {data.ai_recap && (
            <div style={{ background: '#eff6ff', borderLeft: '4px solid #2563eb', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                <FiZap size={12} /> Summary
              </div>
              <div style={{ fontSize: 15, lineHeight: 1.55, color: '#1f2937' }}>{data.ai_recap}</div>
            </div>
          )}

          {/* Totals */}
          <div style={{ display: 'flex', flexWrap: 'wrap', background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 16 }}>
            <Stat icon={<FiFileText size={11} />} value={data.totals.jobs} label="Jobs" />
            <Stat icon={<FiFileText size={11} />} value={data.totals.notes} label="Notes" />
            <Stat icon={<FiCamera size={11} />} value={data.totals.photos} label="Photos" />
            <Stat icon={<FiClock size={11} />} value={data.totals.hours} label="Hours" />
          </div>

          {/* Job cards */}
          {data.jobs.map((j) => (
            <div key={j.job_id} style={{ background: '#fff', borderRadius: 12, padding: 16, marginBottom: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'baseline' }}>
                <Link to={`/jobs/${j.job_id}`} style={{ fontSize: 16, fontWeight: 700, color: '#2563eb', textDecoration: 'none' }}>
                  {j.job_number}{j.client_name ? ` — ${j.client_name}` : ''}
                </Link>
                <span style={{ fontSize: 11, fontWeight: 600, background: '#eff6ff', color: '#1d4ed8', padding: '3px 9px', borderRadius: 10 }}>{j.status}</span>
              </div>
              {j.property_address && <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>{j.property_address}</div>}
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {j.crew.length > 0 && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><FiUsers size={12} /> {j.crew.join(', ')}</span>}
                {j.hours > 0 && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><FiClock size={12} /> {j.hours} hrs</span>}
                {j.work_types.length > 0 && <span>{j.work_types.join(', ')}</span>}
              </div>

              {j.notes.length > 0 && (
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {j.notes.map((n, i) => (
                    <div key={i} style={{ fontSize: 14, color: '#374151', background: '#f9fafb', borderRadius: 6, padding: '8px 10px' }}>
                      <span style={{ fontWeight: 600 }}>{n.member}</span>
                      <span style={{ color: '#9ca3af', fontSize: 12 }}> · {fmtTime(n.time)}</span>
                      <div style={{ marginTop: 2, whiteSpace: 'pre-wrap' }}>{n.text}</div>
                    </div>
                  ))}
                </div>
              )}

              {j.photos.length > 0 && (
                <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 8 }}>
                  {j.photos.map((p) => (
                    <a key={p.id} href={`${API_BASE_URL}${p.url}`} target="_blank" rel="noopener noreferrer"
                      style={{ display: 'block', borderRadius: 6, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                      <img src={`${API_BASE_URL}${p.url}`} alt={p.caption || 'Job photo'} loading="lazy"
                        style={{ width: '100%', height: 80, objectFit: 'cover', display: 'block' }} />
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
}
